import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Eye, Landmark, Droplets, Star, Castle, Skull, Camera, Info, Mountain, ExternalLink, Loader2 } from 'lucide-react';
import type { DiscoverPOI, Coordinates } from '../../types';
import { getDistance } from '../../services/geometryUtils';
import { useLocationImageState } from '../../hooks/useLocationImage';

const CATEGORY_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  viewpoint: Eye,
  waterfall: Droplets,
  museum: Landmark,
  monument: Star,
  memorial: Star,
  castle: Castle,
  ruins: Skull,
  attraction: Star,
  information: Info,
  peak: Mountain,
  cultural_heritage: Landmark,
};

const CATEGORY_COLORS: Record<string, string> = {
  viewpoint: 'bg-emerald-500/15 text-emerald-400',
  waterfall: 'bg-cyan-500/15 text-cyan-400',
  museum: 'bg-amber-500/15 text-amber-400',
  monument: 'bg-rose-500/15 text-rose-400',
  memorial: 'bg-rose-500/15 text-rose-400',
  castle: 'bg-amber-500/15 text-amber-400',
  ruins: 'bg-stone-500/15 text-stone-400',
  attraction: 'bg-rose-500/15 text-rose-400',
  information: 'bg-blue-500/15 text-blue-400',
  peak: 'bg-emerald-500/15 text-emerald-400',
  cultural_heritage: 'bg-amber-500/15 text-amber-400',
};

interface DiscoverCardProps {
  poi: DiscoverPOI;
  center: Coordinates;
  isSelected?: boolean;
  onSelect: (id: string, lat: number, lon: number) => void;
}

const DiscoverCard: React.FC<DiscoverCardProps> = ({ poi, center, isSelected = false, onSelect }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement>(null);
  const Icon = CATEGORY_ICONS[poi.category] || Star;
  const colorClass = CATEGORY_COLORS[poi.category] || 'bg-stone-500/15 text-stone-400';

  const distMeters = getDistance(center.latitude, center.longitude, poi.lat, poi.lon);
  const distKm = (distMeters / 1000).toFixed(1);

  // Fall back to the category icon if the thumbnail fails to load (dead OSM
  // `image` links, 404s, hotlink blocks). Reset when the POI's image changes
  // so a recycled card instance doesn't stay stuck on the icon.
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [poi.imageUrl]);
  const showImage = !!poi.imageUrl && !imgFailed;

  // Fallback for POI-er uten (eller med død) imageUrl: stedsnøyaktig Mapillary /
  // Street View. Kun for det valgte kortet → én oppslag av gangen (billig).
  const { image: locImg, loading: locImgLoading } = useLocationImageState(poi.lat, poi.lon, isSelected && !showImage);
  const displayUrl = showImage ? poi.imageUrl : (locImg?.url || null);
  // Vis lasteanimasjon i bilde-sporet mens det stedsnøyaktige bildet hentes
  // (backend-oppslaget kan ta et par sekunder på første treff) — ellers ser det
  // ut som ingenting skjer etter at brukeren trykker på kortet.
  const showImageSpinner = isSelected && !displayUrl && locImgLoading;

  useEffect(() => {
    if (isSelected && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isSelected]);

  return (
    <button
      ref={ref}
      onClick={() => onSelect(poi.id, poi.lat, poi.lon)}
      className={`w-full flex flex-col gap-2 p-3 rounded-xl transition-all text-left group ${
        isSelected
          ? 'bg-amber-500/10 ring-1 ring-amber-500/40'
          : 'bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)]'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Image, loading spinner, or icon fallback */}
        {displayUrl ? (
          <div className={`flex-shrink-0 rounded-lg overflow-hidden bg-black/20 ${isSelected ? 'w-20 h-20' : 'w-16 h-16'} transition-all`}>
            <img
              src={displayUrl}
              alt={poi.name}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={() => { if (showImage) setImgFailed(true); }}
            />
          </div>
        ) : showImageSpinner ? (
          <div className={`flex-shrink-0 w-20 h-20 rounded-lg flex items-center justify-center bg-black/20 transition-all`}>
            <Loader2 size={22} className="animate-spin text-amber-400" />
          </div>
        ) : (
          <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${colorClass}`}>
            <Icon size={20} />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-semibold truncate transition-colors ${
            isSelected ? 'text-amber-400' : 'text-[var(--text-primary)] group-hover:text-amber-400'
          }`}>
            {poi.name}
          </h3>
          {poi.description && !isSelected && (
            <p className="text-xs text-[var(--text-secondary)] line-clamp-2 mt-0.5">
              {poi.description}
            </p>
          )}
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-3xs font-bold uppercase ${colorClass}`}>
              {t(`discover.${poi.category}`, poi.category)}
            </span>
            {poi.source === 'user' && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-4xs font-bold bg-pink-500/15 text-pink-400">
                {t('discover.user_contribution', 'Brukerbidrag')}
              </span>
            )}
            <span className="flex items-center gap-0.5 text-2xs text-[var(--text-muted)]">
              <MapPin size={10} />
              {t('discover.distance', { km: distKm })}
            </span>
            {poi.municipality && (
              <span className="text-2xs text-[var(--text-muted)]">
                {poi.municipality}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded content when selected */}
      {isSelected && (poi.description || poi.url) && (
        <div className="px-1 pt-1 border-t border-amber-500/20">
          {poi.description && (
            <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
              {poi.description}
            </p>
          )}
          <div className="flex items-center gap-3 mt-1">
            {poi.url && (
              <a
                href={poi.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-2xs font-semibold text-amber-400 hover:text-amber-300 transition-colors"
              >
                <ExternalLink size={10} />
                {t('discover.read_more', 'Les mer')}
              </a>
            )}
            {poi.photographer && (
              <span className="flex items-center gap-0.5 text-3xs text-[var(--text-muted)]">
                <Camera size={8} />
                {poi.photographer}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Photographer credit when not expanded */}
      {!isSelected && poi.photographer && (
        <span className="flex items-center gap-0.5 text-3xs text-[var(--text-muted)] -mt-1 px-0">
          <Camera size={8} />
          {poi.photographer}
        </span>
      )}
    </button>
  );
};

export default React.memo(DiscoverCard);
